import React, { useState } from "react"
import { FieldError, useFieldArray, useFormContext } from "react-hook-form"
import { FormItem, FormControl, FormMessage } from "../ui/form"
import { Button } from "../ui/button"
import { Box, Users, PenTool as Tool, Plus, Trash2, Languages } from "lucide-react"
import { WorkLogFormData } from "@pma/utils/work-diaries-schema"
import { FormLabel } from "../../form"
import { AutoCompleteInput } from "./auto-completeInput"
import useTranslator from "@pma/hooks/use-translator"
import { RequiredFieldLabel } from "../ui/required-field-label"

type SectionProps = {
  icon: React.ReactNode
  title: string
  fields: {
    type: string
    quantity: number
    id?: number | undefined
    actionType?: "add" | "edit" | "delete" | undefined
    remarks?: string | undefined
  }[]
  onAdd: () => void
  onRemove: (index: number) => void
  addLabel: string
  buttonClass: string
  renderFields: (index: number, onResourceChange?: () => void) => React.ReactNode
  renderTranslationField: (index: number) => React.ReactNode
  translationLabel: string
  onTranslateClick: (index: number) => void
}
interface ResourcesFormProps {
  lstStructures: any[]
  lstWorkforce: any[]
  lstTools: any[]
}

export function ResourcesForm({ lstStructures, lstWorkforce, lstTools }: ResourcesFormProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const {
    setValue,
    getValues,
    register,
    formState: { errors },
    control,
  } = useFormContext<WorkLogFormData>()

  const siteResources = useFieldArray({
    control,
    name: "siteResources",
  })

  const workforce = useFieldArray({
    control,
    name: "workforce",
  })

  const equipment = useFieldArray({
    control,
    name: "equipment",
  })
  const removeMap = {
    siteResources: siteResources.remove,
    workforce: workforce.remove,
    equipment: equipment.remove,
  }

  const onRemove = (index: number, type: "siteResources" | "workforce" | "equipment") => {
    const currentDetails = getValues(type)
    const item = currentDetails && currentDetails[index]

    if (item?.id && currentDetails) {
      // Mark existing item for deletion
      const updated = [...currentDetails]
      updated[index] = { ...updated[index], actionType: "delete" }
      setValue(type, updated)
    } else {
      // Remove newly added (unsaved) item
      removeMap[type](index)
    }
  }
  const oppositeLanguageLabel = isHebrew ? translate("English") : translate("Hebrew")

  const getOppositeLanguageValue = (typeName: string | number, options: any[]) => {
    if (typeof typeName === "string") return typeName
    const resource = options.find((obj: any) => obj.id === typeName)
    if (!resource) return ""
    return isHebrew
      ? resource.en || resource.he || ""
      : resource.he || resource.en || ""
  }

  return (
    <div className='space-y-8'>
      <Section
        icon={<Box className='h-5 w-5 text-emerald-500' />}
        title={translate("Site Resources")}
        fields={siteResources.fields}
        onAdd={() =>
          siteResources.append({
            typeName: "",
            translatedName: "",
            type: "structures",
            remarks: "",
            quantity: 0,
            actionType: "add",
          })
        }
        onRemove={(index) => onRemove(index, "siteResources")}
        addLabel={translate("Add Resource")}
        buttonClass='bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200'
        translationLabel={oppositeLanguageLabel}
        onTranslateClick={(index) => {
          const typeName = getValues(`siteResources.${index}.typeName`)
          setValue(`siteResources.${index}.translatedName`, getOppositeLanguageValue(typeName, lstStructures))
        }}
        renderFields={(index, onResourceChange) => (
          <>
            <div>
              <FormLabel className='mb-1 flex items-center gap-1 text-sm font-medium text-gray-700'>
                {translate("Resource")} <RequiredFieldLabel tooltipText={translate("Resource is required")} />
              </FormLabel>
              <AutoCompleteInput
                name={`siteResources.${index}.typeName`}
                options={lstStructures
                  .filter((obj) => obj.structures == true)
                  .map((obj: any) => {
                    return {
                      value: isHebrew
                        ? ["", null, undefined].includes(obj.he)
                          ? obj.en
                          : obj.he
                        : obj.en,
                      id: obj.id,
                    }
                  })}
                value={getValues(`siteResources.${index}.typeName`)}
                freesolo={true}
                required
                onChange={onResourceChange}
                showAllOnSelect
              />
            </div>
            <FormItem className='relative' error={errors.siteResources?.[index]?.remarks as FieldError | undefined}>
              <FormLabel>{translate("Remarks")}</FormLabel>
              <FormControl
                {...register(`siteResources.${index}.remarks` as const)}
                placeholder={`${translate("Name")}/${translate("Model")}`}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>

            <FormItem className='relative' error={errors.siteResources?.[index]?.quantity as FieldError | undefined}>
              <FormLabel>
                {translate("Quantity")} <RequiredFieldLabel tooltipText={translate("Quantity is required")} />
              </FormLabel>
              <FormControl
                type='number'
                {...register(`siteResources.${index}.quantity` as const, { valueAsNumber: true })}
                placeholder={translate("Quantity")}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>
          </>
        )}
        renderTranslationField={(index) => (
          <FormItem>
            <FormControl
              {...register(`siteResources.${index}.translatedName` as const)}
              placeholder={`${translate("Name")} (${oppositeLanguageLabel})`}
              dir={isHebrew ? "ltr" : "rtl"}
            />
          </FormItem>
        )}
      />

      <Section
        icon={<Users className='h-5 w-5 text-amber-500' />}
        title={translate("Workforce")}
        fields={workforce.fields}
        onAdd={() =>
          workforce.append({
            typeName: "",
            translatedName: "",
            type: "employees",
            quantity: 0,
            remarks: "",
            actionType: "add",
          })
        }
        onRemove={(index) => onRemove(index, "workforce")}
        addLabel={translate("Add Workers")}
        buttonClass='bg-amber-50 text-amber-600 hover:bg-amber-100 border-amber-200'
        translationLabel={oppositeLanguageLabel}
        onTranslateClick={(index) => {
          const typeName = getValues(`workforce.${index}.typeName`)
          setValue(`workforce.${index}.translatedName`, getOppositeLanguageValue(typeName, lstWorkforce))
        }}
        renderFields={(index, onResourceChange) => (
          <>
            <div>
              <FormLabel className='mb-1 flex items-center gap-1 text-sm font-medium text-gray-700'>
                {translate("Worker")} <RequiredFieldLabel tooltipText={translate("Worker is required")} />
              </FormLabel>
              <AutoCompleteInput
                name={`workforce.${index}.typeName`}
                options={lstWorkforce
                  .filter((obj) => obj.employees == true)
                  .map((obj: any) => {
                    return {
                      value: isHebrew
                        ? ["", null, undefined].includes(obj.he)
                          ? obj.en
                          : obj.he
                        : obj.en,
                      id: obj.id,
                    }
                  })}
                value={getValues(`workforce.${index}.typeName`)}
                freesolo={true}
                required
                onChange={onResourceChange}
                showAllOnSelect
              />
            </div>
            <FormItem className='relative' error={errors.workforce?.[index]?.quantity as FieldError | undefined}>
              <FormLabel>
                {translate("Quantity")} <RequiredFieldLabel tooltipText={translate("Quantity is required")} />
              </FormLabel>
              <FormControl
                type='number'
                {...register(`workforce.${index}.quantity` as const, { valueAsNumber: true })}
                placeholder={translate("Quantity")}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>

            <FormItem className='relative' error={errors.workforce?.[index]?.remarks as FieldError | undefined}>
              <FormLabel>{translate("Remarks")}</FormLabel>
              <FormControl
                {...register(`workforce.${index}.remarks` as const)}
                placeholder={translate("Remarks")}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>
          </>
        )}
        renderTranslationField={(index) => (
          <FormItem>
            <FormControl
              {...register(`workforce.${index}.translatedName` as const)}
              placeholder={`${translate("Name")} (${oppositeLanguageLabel})`}
              dir={isHebrew ? "ltr" : "rtl"}
            />
          </FormItem>
        )}
      />

      <Section
        icon={<Tool className='h-5 w-5 text-purple-500' />}
        title={`${translate("Equipment")} & ${translate("Tools")}`}
        fields={equipment.fields}
        onAdd={() =>
          equipment.append({
            typeName: "",
            translatedName: "",
            type: "tools",
            quantity: 0,
            remarks: "",
            actionType: "add",
          })
        }
        onRemove={(index) => onRemove(index, "equipment")}
        addLabel={translate("Add Equipment")}
        buttonClass='bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200'
        translationLabel={oppositeLanguageLabel}
        onTranslateClick={(index) => {
          const typeName = getValues(`equipment.${index}.typeName`)
          setValue(`equipment.${index}.translatedName`, getOppositeLanguageValue(typeName, lstTools))
        }}
        renderFields={(index, onResourceChange) => (
          <>
            <div>
              <FormLabel className='mb-1 flex items-center gap-1 text-sm font-medium text-gray-700'>
                {translate("Equipment")} <RequiredFieldLabel tooltipText={translate("Equipment is required")} />
              </FormLabel>
              <AutoCompleteInput
                name={`equipment.${index}.typeName`}
                options={lstTools
                  .filter((obj) => obj.tools == true)
                  .map((obj: any) => {
                    return {
                      value: isHebrew
                        ? ["", null, undefined].includes(obj.he)
                          ? obj.en
                          : obj.he
                        : obj.en,
                      id: obj.id,
                    }
                  })}
                value={getValues(`equipment.${index}.typeName`)}
                freesolo={true}
                required
                onChange={onResourceChange}
                showAllOnSelect
              />
            </div>
            <FormItem className='relative' error={errors.equipment?.[index]?.quantity as FieldError | undefined}>
              <FormLabel>
                {translate("Quantity")} <RequiredFieldLabel tooltipText={translate("Quantity is required")} />
              </FormLabel>
              <FormControl
                type='number'
                {...register(`equipment.${index}.quantity` as const, { valueAsNumber: true })}
                placeholder={translate("Quantity")}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>

            <FormItem className='relative' error={errors.equipment?.[index]?.remarks as FieldError | undefined}>
              <FormLabel>{translate("Remarks")}</FormLabel>
              <FormControl
                {...register(`equipment.${index}.remarks` as const)}
                placeholder={translate("Remarks")}
              />
              <FormMessage className='absolute -bottom-5 start-0' />
            </FormItem>
          </>
        )}
        renderTranslationField={(index) => (
          <FormItem>
            <FormControl
              {...register(`equipment.${index}.translatedName` as const)}
              placeholder={`${translate("Name")} (${oppositeLanguageLabel})`}
              dir={isHebrew ? "ltr" : "rtl"}
            />
          </FormItem>
        )}
      />
    </div>
  )
}

function Section({
  icon,
  title,
  fields,
  onAdd,
  onRemove,
  addLabel,
  buttonClass,
  renderFields,
  renderTranslationField,
  translationLabel,
  onTranslateClick,
}: SectionProps) {
  const [openTranslations, setOpenTranslations] = useState<Record<number, boolean>>({})

  const toggleTranslation = (index: number) => {
    const isOpening = !openTranslations[index]
    setOpenTranslations((prev) => ({ ...prev, [index]: isOpening }))
    if (isOpening) {
      onTranslateClick(index)
    }
  }

  return (
    <div className='space-y-4 rounded-lg border border-gray-200 p-4 shadow-sm'>
      <div className='flex items-center gap-2'>
        {icon}
        <h3 className='text-lg font-medium'>{title}</h3>
      </div>

      <div className='space-y-3'>
        <Button type='button' variant='outline' size='sm' className={buttonClass} onClick={onAdd}>
          <Plus className='mr-2 h-4 w-4' />
          {addLabel}
        </Button>

        {fields.map((field, index) =>
          field.actionType === "delete" ? null : (
            <div key={field.id} className='space-y-2'>
              <div className='grid grid-cols-1 items-end gap-3 rounded-md border border-gray-200 bg-white p-3 pb-6 md:grid-cols-[1fr,120px,1fr,80px]'>
                {renderFields(index, () => {
                  if (openTranslations[index]) {
                    setTimeout(() => onTranslateClick(index), 0)
                  }
                })}
                <div className='mb-1 flex gap-1'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => toggleTranslation(index)}
                    title={translationLabel}
                  >
                    <Languages className='h-4 w-4 text-blue-500' />
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => onRemove(index)}
                  >
                    <Trash2 className='h-4 w-4 text-red-500' />
                  </Button>
                </div>
              </div>
              {openTranslations[index] && (
                <div className='rounded-md border border-blue-200 bg-blue-50 p-3'>
                  <p className='mb-2 text-sm font-medium text-blue-700'>
                    {translationLabel}
                  </p>
                  {renderTranslationField(index)}
                </div>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  )
}
